import { Order, PreferencesState, SelectItem } from '@abb-procure/data';
import { OverlayRef } from '@angular/cdk/overlay';
import { Component, DestroyRef, InjectionToken, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { FormRowDirective } from '../../directives/form-row.directive';
import { Button } from '../button/button.component';
import { Card } from '../card/card.component';
import { InputCheck } from '../input-check/input-check.component';
import { InputSelect } from '../input-select/input-select.component';

type OrderOverlayForm = {
  orderIsAscending: FormControl<boolean>;
  orderBy: FormControl<SelectItem>;
};

type OrderOverlayData = {
  preference: string;
  orderByOptions: SelectItem[];
  entityType: string;
  value: Order | null;
};

export const ORDER_OVERLAY_CONTAINER = new InjectionToken<OverlayRef>(
  'ORDER_OVERLAY_CONTAINER',
);
export const ORDER_OVERLAY_DATA = new InjectionToken<OrderOverlayData>(
  'ORDER_OVERLAY_DATA',
);

@Component({
  templateUrl: './order-overlay.component.html',
  styleUrl: './order-overlay.component.scss',
  imports: [FormRowDirective, InputSelect, InputCheck, Button, Card],
})
export class OrderOverlay {
  protected form!: FormGroup<OrderOverlayForm>;
  protected rootClass = 'order-overlay';
  protected readonly data: OrderOverlayData = inject(ORDER_OVERLAY_DATA);

  private ignoreClick: boolean = true;
  private readonly container: OverlayRef = inject(ORDER_OVERLAY_CONTAINER);
  private readonly formBuilder = inject(FormBuilder);
  private readonly preferencesState = inject(PreferencesState);

  constructor() {
    this.defineFormFields();
    this.onGlobalClick = this.onGlobalClick.bind(this);
    document.addEventListener('click', this.onGlobalClick);

    inject(DestroyRef).onDestroy(() => {
      document.removeEventListener('click', this.onGlobalClick);
    });
  }

  apply(): void {
    const value = this.form.getRawValue();
    const { entityType } = this.data;

    this.preferencesState.updatePreferences({
      key: entityType
        ? `${this.data.preference}-${this.data.entityType}`
        : this.data.preference,
      value: JSON.stringify({
        orderBy: value.orderBy.value,
        orderIsAscending: value.orderIsAscending,
      }),
    });
    this.container.dispose();
  }

  reset(): void {
    this.form.reset({
      orderBy: this.data.orderByOptions?.[0],
      orderIsAscending: true,
    });

    this.apply();
  }

  private defineFormFields(): void {
    const { value } = this.data;

    this.form = this.formBuilder.group<OrderOverlayForm>({
      orderBy: this.formBuilder.nonNullable.control(
        this.data.orderByOptions?.find(
          (option) => option.value === value?.orderBy,
        ) ?? this.data.orderByOptions?.[0],
      ),
      orderIsAscending: this.formBuilder.nonNullable.control(
        value?.orderIsAscending ?? true,
      ),
    });
  }

  private onGlobalClick(event: MouseEvent): void {
    if (this.ignoreClick) {
      this.ignoreClick = false;
      return;
    }

    let element = event.target as HTMLElement;

    while (
      element &&
      !element.classList.contains(this.rootClass) &&
      !element.classList.contains('ng-dropdown-panel') &&
      !element.classList.contains('tag')
    ) {
      element = element.parentElement as HTMLElement;
    }

    if (element) {
      return;
    }

    if (this.form.dirty) {
      this.apply();
    }

    this.container.dispose();
    document.removeEventListener('click', this.onGlobalClick);
  }
}
